import React, { useEffect, useState } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { supabase, supabaseConfigError } from '../../../config/supabase'
import SafeIcon from '../../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiUserPlus, FiUserMinus, FiUsers, FiRefreshCw } = FiIcons

const AssignToStudents = ({ curriculum, onAssigned }) => {
  const { userProfile } = useAuth()
  const [children, setChildren] = useState([])
  const [assignments, setAssignments] = useState([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState(null)

  const curriculumId = curriculum?.id
  const isParent = userProfile?.role === 'parent'

  useEffect(() => {
    if (curriculumId && userProfile) {
      fetchChildren()
      fetchAssignments()
    } else {
      setChildren([])
      setAssignments([])
    }
  }, [curriculumId, userProfile])

  const fetchChildren = async () => {
    if (supabaseConfigError || !isParent) return
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, user_id, first_name, last_name')
        .eq('parent_id', userProfile.id)
        .eq('role', 'student')

      if (error) throw error
      setChildren(data || [])
    } catch (err) {
      console.error('Error fetching children:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAssignments = async () => {
    if (supabaseConfigError || !curriculumId) return
    try {
      const { data, error } = await supabase
        .from('curriculum_student_assignments')
        .select('student_id')
        .eq('curriculum_id', curriculumId)

      if (error) throw error
      setAssignments(data || [])
    } catch (err) {
      console.error('Error fetching assignments:', err)
    }
  }

  const isAssigned = (childUserId) =>
    assignments.some((a) => a.student_id === childUserId)

  const handleAssign = async (child, assign) => {
    if (supabaseConfigError || !curriculumId) return
    setAssigning(child.user_id)
    try {
      if (assign) {
        const { error } = await supabase
          .from('curriculum_student_assignments')
          .insert({
            curriculum_id: curriculumId,
            student_id: child.user_id,
            assigned_by: userProfile?.user_id || null
          })

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('curriculum_student_assignments')
          .delete()
          .eq('curriculum_id', curriculumId)
          .eq('student_id', child.user_id)

        if (error) throw error
      }
      await fetchAssignments()
      onAssigned?.()
    } catch (err) {
      console.error('Error updating assignment:', err)
    } finally {
      setAssigning(null)
    }
  }

  if (!curriculumId || !isParent) return null

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <SafeIcon icon={FiUsers} className="w-5 h-5 text-[#00AAFF]" />
        <h3 className="font-semibold text-gray-800">Assign to students</h3>
      </div>
      <div className="p-5">
        <p className="text-sm text-gray-600 mb-4">
          Assign this curriculum so your children see its lessons in their dashboard.
        </p>
        {loading ? (
          <div className="space-y-2">
            <div className="h-10 bg-gray-100 rounded animate-pulse" />
            <div className="h-10 bg-gray-100 rounded animate-pulse" />
          </div>
        ) : children.length === 0 ? (
          <p className="text-sm text-gray-500">
            Add children from My Children first.
          </p>
        ) : (
          <div className="space-y-2">
            {children.map((child) => {
              const assigned = isAssigned(child.user_id)
              const busy = assigning === child.user_id
              return (
                <div
                  key={child.user_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50"
                >
                  <span className="font-medium text-gray-800">
                    {child.first_name} {child.last_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAssign(child, !assigned)}
                    disabled={busy}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                      assigned
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'bg-[#00AAFF] text-white hover:bg-[#0099E5]'
                    }`}
                  >
                    {busy ? (
                      <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                    ) : assigned ? (
                      <>
                        <SafeIcon icon={FiUserMinus} className="w-4 h-4" />
                        Unassign
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiUserPlus} className="w-4 h-4" />
                        Assign
                      </>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AssignToStudents
